// src/components/timeBoxes/AIRecommendationCard.tsx
import React from 'react';
import { TimeRecommendation } from '../../../types/scheduleRecommendation';
import { Calendar, Clock } from '../../../components/common/icons';
import { formatTimeToString } from '../../../utils/dateUtils';

interface AIRecommendationCardProps {
  recommendation: TimeRecommendation;
  isSelected?: boolean;
  onSelect: () => void;
}

/**
 * AI 時間建議卡片組件
 * 顯示單個 AI 推薦的時間選項
 */
const AIRecommendationCard: React.FC<AIRecommendationCardProps> = ({
  recommendation,
  isSelected = false,
  onSelect
}) => {
  const { startTime, endTime, reason } = recommendation;

  // 格式化顯示時間
  const formatDisplayTime = (date: Date) => {
    const isToday = new Date().toDateString() === date.toDateString();
    const isTomorrow =
      new Date(Date.now() + 86400000).toDateString() === date.toDateString();

    let dayLabel = '';
    if (isToday) {
      dayLabel = 'Today';
    } else if (isTomorrow) {
      dayLabel = 'Tomorrow';
    } else {
      // 格式化為 "Mon, Jan 15"
      dayLabel = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }

    const timeStr = formatTimeToString(date);
    return { dayLabel, timeStr };
  };

  const start = formatDisplayTime(startTime);
  const end = formatDisplayTime(endTime);

  // 計算持續時間
  const durationMinutes = Math.round(
    (endTime.getTime() - startTime.getTime()) / 60000
  );
  const durationDisplay =
    durationMinutes >= 60
      ? `${Math.floor(durationMinutes / 60)}h ${durationMinutes % 60}m`
      : `${durationMinutes}m`;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full p-4 rounded-lg border-2 text-left transition-all hover:shadow-md ${
        isSelected
          ? 'border-sky-500 bg-sky-50 shadow-sm'
          : 'border-gray-200 bg-white hover:border-sky-300'
      }`}
    >
      {/* 日期和時間 */}
      <div className="flex items-center gap-2 mb-2">
        <Calendar size={16} className={isSelected ? 'text-sky-600' : 'text-gray-500'} />
        <span className={`font-semibold ${isSelected ? 'text-sky-900' : 'text-gray-900'}`}>
          {start.dayLabel}
        </span>
      </div>

      {/* 時間範圍 */}
      <div className="flex items-center gap-2 mb-2">
        <Clock size={16} className={isSelected ? 'text-sky-600' : 'text-gray-500'} />
        <span className={`text-sm ${isSelected ? 'text-sky-800' : 'text-gray-700'}`}>
          {start.timeStr} - {end.timeStr}
        </span>
        <span
          className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
            isSelected
              ? 'bg-sky-200 text-sky-800'
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {durationDisplay}
        </span>
      </div>

      {/* 建議理由 */}
      <div className={`text-sm ${isSelected ? 'text-sky-700' : 'text-gray-600'}`}>
        💡 {reason}
      </div>
    </button>
  );
};

export default AIRecommendationCard;
